import { type Draft, type Objectish } from "immer";
import {
	type FC,
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
} from "react";

export type DoTransactionFn<TState extends Objectish> = (draft: Draft<TState>) => void;

export interface CellServicesContextState {
	cellId: string;
	cellName: string;
	currentUser: { id: string; name: string; profilePicture: string } | null;
	users: { id: string; name: string; profilePicture: string }[];
	pageStorage: Objectish;
	pageStorageCommit: (doTransaction: DoTransactionFn<Objectish>) => Promise<void>;
	documentInteractionMode: "static" | "interactive";
}

const CellServicesContext = createContext<CellServicesContextState>({
	cellId: "",
	cellName: "",
	users: [],
	pageStorage: {},
	pageStorageCommit: () => Promise.resolve(),
	documentInteractionMode: "interactive",
	currentUser: null,
});

export const useCellServices = () => useContext(CellServicesContext);

export const CellServicesContextProvider: FC<
	PropsWithChildren & {
		cellId: string;
		cellName: string;
		pageStorage: Objectish;
		pageStorageCommit: (doTransaction: DoTransactionFn<Objectish>) => Promise<void>;
		documentInteractionMode: "static" | "interactive";
		currentUser: { id: string; name: string; profilePicture: string } | null;
	}
> = ({
	children,
	cellId,
	cellName,
	pageStorage,
	pageStorageCommit: _pageStorageCommit,
	documentInteractionMode,
	currentUser,
}) => {
	// `pageStorageCommit` is always a normal, non-Reactified function.
	//
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const pageStorageCommit = useCallback(_pageStorageCommit, []);

	const value: CellServicesContextState = useMemo(
		() => ({
			cellId,
			cellName,
			users: currentUser ? [currentUser] : [],
			pageStorage,
			pageStorageCommit,
			// documentInteractionMode: "static",
			documentInteractionMode,
			currentUser,
		}),
		[cellId, cellName, pageStorage, pageStorageCommit, documentInteractionMode, currentUser]
	);
	return <CellServicesContext.Provider value={value}>{children}</CellServicesContext.Provider>;
};

export const useCellState = (): [
	Objectish,
	(doTransaction: DoTransactionFn<Objectish>) => Promise<void>,
] => {
	const { cellId, pageStorage, pageStorageCommit } = useCellServices();

	const setCellState = useCallback(
		(doTransaction: DoTransactionFn<Objectish>) =>
			pageStorageCommit((store) => {
				store[cellId] = doTransaction(store[cellId] ?? {});
			}),
		[cellId, pageStorageCommit]
	);

	return useMemo(
		() => [pageStorage?.[cellId], setCellState],
		[cellId, pageStorage, setCellState]
	);
};
